import { Navigate, Route, Routes } from 'react-router-dom'

import { LoginPage } from './modules/login'
import { OAuthCallbackPage } from './modules/oauth-callback'
import { DashboardPage } from './modules/dashboard'
import { WizardPage } from './modules/wizard'
import { CustomersListPage } from './modules/customers/list'
import { CustomerEditPage } from './modules/customers/edit'
import { ProductsListPage } from './modules/products/list'
import { ProductEditPage } from './modules/products/edit'
import { InvoicesListPage } from './modules/invoices/list'
import { InvoiceCreatePage } from './modules/invoices/create'
import { InvoiceDetailPage } from './modules/invoices/detail'
import { ReportsPage } from './modules/reports'
import { SettingsPage } from './modules/settings'
import { ProfileEditPage } from './modules/settings/profile-edit'
import { ResolutionEditPage } from './modules/settings/resolution-edit'
import { RegisterCompanyPage } from './modules/register-company'
import { AccountProfilePage } from './modules/account/profile'
import { AccountSecurityPage } from './modules/account/security'
import { AccountPrivacyPage } from './modules/account/privacy'
import { HabilitacionPage } from './modules/habilitacion'
import { ProtectedRoute } from './components/layout/protected-route'
import { ROUTES } from './lib/constants'
import { hasValidAccessToken } from './lib/api'

/**
 * HomeRedirect — la URL `/` en app.taxor.co.
 *
 * Decisión arquitectónica enterprise (CEO 2026-05-13): app.taxor.co
 * es un dominio 100% autenticado. La landing pública de marketing
 * vive en taxor.co (tax-website). En app.taxor.co `/` no muestra
 * página pública alguna — siempre redirige:
 *   - Si hay JWT válido en memoria → /dashboard.
 *   - Si NO hay token → /login. (ProtectedRoute hace el silent refresh
 *     después si la cookie httpOnly de refresh sigue activa.)
 *
 * Esto blindaje contra cualquier bundle viejo cacheado o cualquier
 * redirect inesperado: ningún flujo deja al user viendo "Iniciar
 * sesión" como si nunca hubiera logueado.
 */
function HomeRedirect() {
  return (
    <Navigate
      to={hasValidAccessToken() ? ROUTES.DASHBOARD : ROUTES.LOGIN}
      replace
    />
  )
}

export default function App() {
  return (
    <Routes>
      {/* Raíz: redirect inteligente, sin landing pública en app.taxor.co. */}
      <Route path={ROUTES.LANDING} element={<HomeRedirect />} />
      <Route path={ROUTES.LOGIN} element={<LoginPage />} />
      {/* OAuth callback — tax-account redirige a /auth/oauth/callback. */}
      <Route path="/auth/oauth/callback" element={<OAuthCallbackPage />} />

      {/* Registro de empresa self-service. Protegido (requiere JWT),
          pero NO requiere active_org (es donde se crea por primera vez). */}
      <Route path={ROUTES.REGISTER_COMPANY} element={<ProtectedRoute><RegisterCompanyPage /></ProtectedRoute>} />

      {/* Autenticadas */}
      <Route path={ROUTES.DASHBOARD} element={<ProtectedRoute><DashboardPage /></ProtectedRoute>} />
      <Route path={ROUTES.WIZARD} element={<ProtectedRoute><WizardPage /></ProtectedRoute>} />

      {/* Catálogos */}
      <Route path={ROUTES.CUSTOMERS} element={<ProtectedRoute><CustomersListPage /></ProtectedRoute>} />
      <Route path={ROUTES.CUSTOMER_NEW} element={<ProtectedRoute><CustomerEditPage /></ProtectedRoute>} />
      <Route path={ROUTES.CUSTOMER_EDIT} element={<ProtectedRoute><CustomerEditPage /></ProtectedRoute>} />
      <Route path={ROUTES.PRODUCTS} element={<ProtectedRoute><ProductsListPage /></ProtectedRoute>} />
      <Route path={ROUTES.PRODUCT_NEW} element={<ProtectedRoute><ProductEditPage /></ProtectedRoute>} />
      <Route path={ROUTES.PRODUCT_EDIT} element={<ProtectedRoute><ProductEditPage /></ProtectedRoute>} />

      {/* Facturación */}
      <Route path={ROUTES.INVOICES} element={<ProtectedRoute><InvoicesListPage /></ProtectedRoute>} />
      <Route path={ROUTES.INVOICE_NEW} element={<ProtectedRoute><InvoiceCreatePage /></ProtectedRoute>} />
      <Route path={ROUTES.INVOICE_DETAIL} element={<ProtectedRoute><InvoiceDetailPage /></ProtectedRoute>} />

      {/* Reportes + settings */}
      <Route path={ROUTES.REPORTS} element={<ProtectedRoute><ReportsPage /></ProtectedRoute>} />
      <Route path={ROUTES.SETTINGS} element={<ProtectedRoute><SettingsPage /></ProtectedRoute>} />
      {/* Sub-páginas settings editables. */}
      <Route path={ROUTES.SETTINGS_PROFILE} element={<ProtectedRoute><ProfileEditPage /></ProtectedRoute>} />
      <Route path={ROUTES.SETTINGS_RESOLUTION} element={<ProtectedRoute><ResolutionEditPage /></ProtectedRoute>} />

      {/* Habilitación DIAN */}
      <Route path={ROUTES.HABILITACION} element={<ProtectedRoute><HabilitacionPage /></ProtectedRoute>} />

      {/* Cuenta personal (perfil + seguridad + privacidad). */}
      <Route path={ROUTES.ACCOUNT} element={<ProtectedRoute><AccountProfilePage /></ProtectedRoute>} />
      <Route path={ROUTES.ACCOUNT_SECURITY} element={<ProtectedRoute><AccountSecurityPage /></ProtectedRoute>} />
      <Route path={ROUTES.ACCOUNT_PRIVACY} element={<ProtectedRoute><AccountPrivacyPage /></ProtectedRoute>} />

      {/* Catch-all: cualquier ruta no definida → HomeRedirect (que decide
          /dashboard o /login según auth). Nunca cae a landing pública. */}
      <Route path="*" element={<HomeRedirect />} />
    </Routes>
  )
}
